/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * Investor Notification Routing Resolver
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Resolves investor notifications to actionable navigation targets.
 * Maps each notification type to:
 *   - Destination route
 *   - Query parameters (panel, entityId, tab)
 *   - CTA label
 *   - Fallback route
 *
 * @module lib/investor/routing/resolver
 */

import type { InvestorNotification, InvestorNotificationType } from '@/types/investorNotifications';
import type {
    InvestorNotificationTarget,
    InvestorPanelType,
    InvestorEntityType,
} from './types';
import { ACTION_REQUIRED_TYPES } from './types';
import { canonicalEntityId } from '@/types/investorEntityReference';

// ═══════════════════════════════════════════════════════════════════════════════
// ROUTE MAPPING
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Configuration for each notification type's routing behavior.
 */
interface NotificationRouteConfig {
    hash?: string;
    route: string;
    panel: InvestorPanelType | null;
    entityType: InvestorEntityType | null;
    ctaLabel: string;
    fallbackRoute: string;
    tab?: string;
}

/**
 * Master mapping of notification types to their routing configuration.
 */
const NOTIFICATION_ROUTE_CONFIG: Record<InvestorNotificationType, NotificationRouteConfig> = {
    // ── Milestones ───────────────────────────────────────────────────────────
    milestone_verified: {
        route: '/investor/milestones',
        panel: 'milestone-review',
        entityType: 'milestone',
        ctaLabel: 'View Milestone',
        fallbackRoute: '/investor/milestones',
    },

    // ── Capital / Distributions ──────────────────────────────────────────────
    distribution_paid: {
        route: '/investor/performance',
        hash: '#distributions',
        panel: 'distribution-detail',
        entityType: 'distribution',
        ctaLabel: 'View Distribution',
        fallbackRoute: '/investor/performance#distributions',
    },

    distribution_pending: {
        route: '/investor/performance',
        hash: '#distributions',
        panel: 'distribution-detail',
        entityType: 'distribution',
        ctaLabel: 'View Distribution',
        fallbackRoute: '/investor/performance#distributions',
    },

    release_completed: {
        // A release is not an investor return. No release-detail projection is
        // connected here; never pass its ID into a distribution drawer.
        route: '/investor/decisions',
        panel: null,
        entityType: null,
        ctaLabel: 'Open Action Centre',
        fallbackRoute: '/investor/decisions',
    },

    // ── Evidence & Documents ─────────────────────────────────────────────────
    evidence_available: {
        route: '/investor/data-rooms',
        panel: 'evidence-preview',
        entityType: 'evidence_pack',
        ctaLabel: 'View Evidence',
        fallbackRoute: '/investor/data-rooms',
        tab: 'evidence_pack',
    },

    dataroom_access_granted: {
        route: '/investor/data-rooms',
        panel: null, // Opens page, no drawer
        entityType: 'dataroom',
        ctaLabel: 'Open Data Room',
        fallbackRoute: '/investor/data-rooms',
    },

    // ── Approvals ────────────────────────────────────────────────────────────
    approval_requested: {
        route: '/investor/data-rooms',
        panel: 'approval-review',
        entityType: 'approval_workflow',
        ctaLabel: 'View Approval Status',
        fallbackRoute: '/investor/data-rooms',
        tab: 'approvals',
    },

    approval_decided: {
        route: '/investor/data-rooms',
        panel: 'approval-review',
        entityType: 'approval_workflow',
        ctaLabel: 'View Decision',
        fallbackRoute: '/investor/data-rooms',
        tab: 'approvals',
    },

    // ── Signatures ───────────────────────────────────────────────────────────
    signature_requested: {
        route: '/investor/data-rooms',
        panel: 'signature-review',
        entityType: 'signature_request',
        ctaLabel: 'Sign Document',
        fallbackRoute: '/investor/data-rooms',
        tab: 'signatures',
    },

    signature_completed: {
        route: '/investor/data-rooms',
        panel: 'signature-review',
        entityType: 'signature_request',
        ctaLabel: 'View Signed Document',
        fallbackRoute: '/investor/data-rooms',
        tab: 'signatures',
    },

    // ── Finance ──────────────────────────────────────────────────────────────
    finance_approval_requested: {
        route: '/investor/decisions',
        panel: 'finance-review',
        entityType: 'finance_approval',
        ctaLabel: 'View Request',
        fallbackRoute: '/investor/decisions',
    },

    finance_approval_decided: {
        route: '/investor/decisions',
        panel: 'finance-review',
        entityType: 'finance_approval',
        ctaLabel: 'View Decision',
        fallbackRoute: '/investor/decisions',
    },

    // ── General ──────────────────────────────────────────────────────────────
    portfolio_update: {
        route: '/investor',
        panel: null,
        entityType: 'project',
        ctaLabel: 'View Portfolio',
        fallbackRoute: '/investor',
    },

    alert: {
        route: '/investor',
        panel: null,
        entityType: null,
        ctaLabel: 'View Details',
        fallbackRoute: '/investor',
    },
};

// ═══════════════════════════════════════════════════════════════════════════════
// RESOLVER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolves an investor notification to its navigation target.
 *
 * @param notification - The notification to resolve
 * @returns Navigation target with route, params, and CTA info
 *
 * @example
 * ```ts
 * const target = getInvestorNotificationTarget(notification);
 * navigate(target.route + buildQueryString(target.queryParams));
 * ```
 */
export function getInvestorNotificationTarget(
    notification: InvestorNotification
): InvestorNotificationTarget {
    const config = NOTIFICATION_ROUTE_CONFIG[notification.type];
    const entityRefMatchesRoute = !notification.entityRef
        || !config.entityType
        || notification.entityRef.entityType === config.entityType;
    const entityId = entityRefMatchesRoute
        ? canonicalEntityId(notification.entityRef, notification.referenceId)
        : undefined;
    const projectId = notification.entityRef?.projectId ?? notification.projectId;

    // Build query parameters
    const queryParams: Record<string, string> = {};

    if (config.panel && entityId) {
        queryParams.panel = config.panel;
        queryParams.entityId = entityId;
    }

    if (config.tab) {
        queryParams.tab = config.tab;
    }

    if (projectId) {
        queryParams.projectId = projectId;
    }

    return {
        route: config.route,
        hash: config.hash,
        queryParams,
        panel: entityId ? config.panel : null,
        entityId: entityId ?? null,
        entityType: config.entityType,
        fallbackRoute: config.fallbackRoute,
        ctaLabel: config.ctaLabel,
        actionRequired: ACTION_REQUIRED_TYPES.has(notification.type),
        tab: config.tab,
    };
}

/**
 * Builds a URL path with query parameters from a notification target.
 *
 * @param target - The resolved notification target
 * @returns Complete URL path with query string
 *
 * @example
 * ```ts
 * const target = getInvestorNotificationTarget(notification);
 * const path = buildNotificationPath(target);
 * // => '/investor/milestones?panel=milestone-review&entityId=ms-123'
 * ```
 */
export function buildNotificationPath(target: InvestorNotificationTarget): string {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(target.queryParams)) {
        if (value) {
            params.set(key, value);
        }
    }

    const queryString = params.toString();
    return `${target.route}${queryString ? `?${queryString}` : ''}${target.hash ?? ''}`;
}

/**
 * Canonical navigation resolver shared by the Header Inbox and full Notifications
 * page. Informational events intentionally return null instead of inventing a
 * destination.
 */
export function resolveInvestorNotificationPath(
    notification: InvestorNotification,
): string | null {
    if (!isNavigableNotification(notification)) return null;
    return buildNotificationPath(getInvestorNotificationTarget(notification));
}

/**
 * Checks if a notification has a navigable target (not just informational).
 *
 * @param notification - The notification to check
 * @returns true if the notification can be navigated to
 */
export function isNavigableNotification(notification: InvestorNotification): boolean {
    const config = NOTIFICATION_ROUTE_CONFIG[notification.type];
    const entityId = canonicalEntityId(notification.entityRef, notification.referenceId);

    // Portfolio updates and alerts without reference are informational only
    if (!entityId && (notification.type === 'portfolio_update' || notification.type === 'alert')) {
        return false;
    }

    return !!config.route;
}

/**
 * Gets the appropriate icon for the notification's action state.
 *
 * @param notification - The notification
 * @returns Icon name for the action state
 */
export function getNotificationActionIcon(notification: InvestorNotification): 'chevronRight' | 'externalLink' | 'pen' | 'check' {
    if (notification.type === 'signature_requested') {
        return 'pen';
    }

    if (ACTION_REQUIRED_TYPES.has(notification.type)) {
        return 'externalLink';
    }

    return 'chevronRight';
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAXONOMY-BASED RESOLVERS
// ═══════════════════════════════════════════════════════════════════════════════

import {
    EVENT_TYPE_META,
    EVENT_CATEGORY_META,
    type EventTypeMeta,
    type EventCategoryMeta,
} from './taxonomy';

/**
 * Get complete event metadata including category info.
 *
 * @param type - Notification type
 * @returns Combined event and category metadata
 */
export function getFullEventMeta(type: InvestorNotificationType): {
    event: EventTypeMeta;
    category: EventCategoryMeta;
} {
    const event = EVENT_TYPE_META[type];
    const category = EVENT_CATEGORY_META[event.category];
    return { event, category };
}

/**
 * Resolve a notification to navigation target using taxonomy metadata.
 * This is a more complete resolver that uses the unified taxonomy.
 *
 * @param notification - The notification to resolve
 * @returns Navigation target with full metadata
 */
export function resolveNotificationWithMeta(notification: InvestorNotification): InvestorNotificationTarget & {
    icon: string;
    categoryLabel: string;
    priority: number;
} {
    const target = getInvestorNotificationTarget(notification);
    const { event, category } = getFullEventMeta(notification.type);

    return {
        ...target,
        icon: event.icon,
        categoryLabel: category.label,
        priority: event.priority,
    };
}

/**
 * Build navigation params for programmatic navigation.
 *
 * @param type - Notification type
 * @param entityId - Entity ID for deep-linking
 * @param projectId - Optional project context
 * @returns Object suitable for navigation
 */
export function buildNavigationParams(
    type: InvestorNotificationType,
    entityId: string,
    projectId?: string
): { pathname: string; search: string; hash?: string } {
    const config = NOTIFICATION_ROUTE_CONFIG[type];
    const params = new URLSearchParams();

    if (config.panel) {
        params.set('panel', config.panel);
        params.set('entityId', entityId);
    }

    if (config.tab) {
        params.set('tab', config.tab);
    }

    if (projectId) {
        params.set('projectId', projectId);
    }

    return {
        pathname: config.route,
        ...(config.hash ? { hash: config.hash } : {}),
        search: params.toString() ? `?${params.toString()}` : '',
    };
}
